/**
 * State Manager for WebSocket Notifications
 * 
 * Phase 3: State Management
 * 
 * Features:
 * - localStorage persistence
 * - State reconciliation on reconnect
 * - Optimistic updates
 * - State conflict resolution
 * 
 * @author BitPanel Team
 * @date 2025-10-28
 */

/**
 * Storage keys for localStorage
 */
export const StorageKeys = {
  NOTIFICATIONS: 'bitpanel_notifications',
  UNREAD_COUNT: 'bitpanel_unread_count',
  LAST_SYNC: 'bitpanel_last_sync',
  CONNECTION_STATE: 'bitpanel_connection_state',
  PENDING_OPERATIONS: 'bitpanel_pending_operations'
};

/**
 * Operation types for pending operations queue
 */
export const OperationType = {
  MARK_READ: 'MARK_READ',
  MARK_ALL_READ: 'MARK_ALL_READ',
  DELETE: 'DELETE',
  UPDATE: 'UPDATE'
};

/**
 * State Manager Class
 * 
 * Manages notification state with localStorage persistence
 */
export class StateManager {
  constructor(options = {}) {
    this.storagePrefix = options.storagePrefix || 'bitpanel_';
    this.maxNotifications = options.maxNotifications || 100;
    this.syncInterval = options.syncInterval || 60000; // 1 minute
    this.enablePersistence = options.enablePersistence !== false;
    
    // Statistics
    this.stats = {
      totalSaves: 0,
      totalLoads: 0,
      totalConflicts: 0,
      totalReconciliations: 0,
      lastSyncTime: null,
      lastLoadTime: null
    };
    
    // Initialize
    this.init();
  }
  
  /**
   * Initialize state manager
   */
  init() {
    if (!this.enablePersistence) {
      console.log('📦 State persistence disabled');
      return;
    }
    
    // Check localStorage availability
    if (!this.isLocalStorageAvailable()) {
      console.warn('⚠️ localStorage not available - persistence disabled');
      this.enablePersistence = false;
      return;
    }
    
    console.log('📦 State manager initialized');
  }
  
  /**
   * Check if localStorage is available
   */
  isLocalStorageAvailable() {
    try {
      const test = '__localStorage_test__';
      localStorage.setItem(test, test);
      localStorage.removeItem(test);
      return true;
    } catch (e) {
      return false;
    }
  }
  
  /**
   * Save notifications to localStorage
   */
  saveNotifications(notifications) {
    if (!this.enablePersistence) {
      return false;
    }
    
    try {
      // Limit to max notifications
      const limited = notifications.slice(0, this.maxNotifications);
      
      const data = {
        notifications: limited,
        timestamp: Date.now(),
        version: '1.0'
      };
      
      localStorage.setItem(StorageKeys.NOTIFICATIONS, JSON.stringify(data));
      
      this.stats.totalSaves++;
      this.stats.lastSyncTime = Date.now();
      
      console.log(`💾 Saved ${limited.length} notifications to localStorage`);
      return true;
    } catch (error) {
      console.error('❌ Failed to save notifications:', error);
      return false;
    }
  }
  
  /**
   * Load notifications from localStorage
   */
  loadNotifications() {
    if (!this.enablePersistence) {
      return null;
    }
    
    try {
      const stored = localStorage.getItem(StorageKeys.NOTIFICATIONS);
      
      if (!stored) {
        console.log('📭 No stored notifications found');
        return null;
      }
      
      const data = JSON.parse(stored);
      
      // Check if data is stale (older than 24 hours)
      const age = Date.now() - data.timestamp;
      const maxAge = 24 * 60 * 60 * 1000; // 24 hours
      
      if (age > maxAge) {
        console.log('🗑️ Stored notifications are stale - clearing');
        this.clearNotifications();
        return null;
      }
      
      this.stats.totalLoads++;
      this.stats.lastLoadTime = Date.now();
      
      console.log(`📬 Loaded ${data.notifications.length} notifications from localStorage`);
      return data.notifications;
    } catch (error) {
      console.error('❌ Failed to load notifications:', error);
      return null;
    }
  }
  
  /**
   * Save unread count to localStorage
   */
  saveUnreadCount(count) {
    if (!this.enablePersistence) {
      return false;
    }
    
    try {
      const data = {
        count: count,
        timestamp: Date.now()
      };
      
      localStorage.setItem(StorageKeys.UNREAD_COUNT, JSON.stringify(data));
      console.log(`💾 Saved unread count: ${count}`);
      return true;
    } catch (error) {
      console.error('❌ Failed to save unread count:', error);
      return false;
    }
  }
  
  /**
   * Load unread count from localStorage
   */
  loadUnreadCount() {
    if (!this.enablePersistence) {
      return null;
    }
    
    try {
      const stored = localStorage.getItem(StorageKeys.UNREAD_COUNT);
      
      if (!stored) {
        return null;
      }
      
      const data = JSON.parse(stored);
      console.log(`📬 Loaded unread count: ${data.count}`);
      return data.count;
    } catch (error) {
      console.error('❌ Failed to load unread count:', error);
      return null;
    }
  }
  
  /**
   * Save connection state to localStorage
   */
  saveConnectionState(state) {
    if (!this.enablePersistence) {
      return false;
    }
    
    try {
      const data = {
        state: state,
        timestamp: Date.now()
      };
      
      localStorage.setItem(StorageKeys.CONNECTION_STATE, JSON.stringify(data));
      return true;
    } catch (error) {
      console.error('❌ Failed to save connection state:', error);
      return false;
    }
  }
  
  /**
   * Load connection state from localStorage
   */
  loadConnectionState() {
    if (!this.enablePersistence) {
      return null;
    }
    
    try {
      const stored = localStorage.getItem(StorageKeys.CONNECTION_STATE);
      
      if (!stored) {
        return null;
      }
      
      const data = JSON.parse(stored);
      return data.state;
    } catch (error) {
      console.error('❌ Failed to load connection state:', error);
      return null;
    }
  }
  
  /**
   * Save pending operation to queue
   */
  savePendingOperation(operation) {
    if (!this.enablePersistence) {
      return false;
    }
    
    try {
      const queue = this.loadPendingOperations() || [];
      
      queue.push({
        ...operation,
        timestamp: Date.now(),
        id: `${operation.type}_${Date.now()}_${Math.random()}`
      });
      
      localStorage.setItem(StorageKeys.PENDING_OPERATIONS, JSON.stringify(queue));
      console.log(`📝 Saved pending operation: ${operation.type}`);
      return true;
    } catch (error) {
      console.error('❌ Failed to save pending operation:', error);
      return false;
    }
  }
  
  /**
   * Load pending operations from queue
   */
  loadPendingOperations() {
    if (!this.enablePersistence) {
      return [];
    }
    
    try {
      const stored = localStorage.getItem(StorageKeys.PENDING_OPERATIONS);
      
      if (!stored) {
        return [];
      }
      
      const queue = JSON.parse(stored);
      console.log(`📬 Loaded ${queue.length} pending operations`);
      return queue;
    } catch (error) {
      console.error('❌ Failed to load pending operations:', error);
      return [];
    }
  }
  
  /**
   * Remove pending operation from queue
   */
  removePendingOperation(operationId) {
    if (!this.enablePersistence) {
      return false;
    }
    
    try {
      const queue = this.loadPendingOperations();
      const filtered = queue.filter(op => op.id !== operationId);
      
      localStorage.setItem(StorageKeys.PENDING_OPERATIONS, JSON.stringify(filtered));
      console.log(`🗑️ Removed pending operation: ${operationId}`);
      return true;
    } catch (error) {
      console.error('❌ Failed to remove pending operation:', error);
      return false;
    }
  }
  
  /**
   * Clear all pending operations
   */
  clearPendingOperations() {
    if (!this.enablePersistence) {
      return false;
    }
    
    try {
      localStorage.removeItem(StorageKeys.PENDING_OPERATIONS);
      console.log('🗑️ Cleared all pending operations');
      return true;
    } catch (error) {
      console.error('❌ Failed to clear pending operations:', error);
      return false;
    }
  }
  
  /**
   * Clear notifications from localStorage
   */
  clearNotifications() {
    if (!this.enablePersistence) {
      return false;
    }
    
    try {
      localStorage.removeItem(StorageKeys.NOTIFICATIONS);
      console.log('🗑️ Cleared stored notifications');
      return true;
    } catch (error) {
      console.error('❌ Failed to clear notifications:', error);
      return false;
    }
  }
  
  /**
   * Clear all stored data
   */
  clearAll() {
    if (!this.enablePersistence) {
      return false;
    }
    
    try {
      Object.values(StorageKeys).forEach(key => {
        localStorage.removeItem(key);
      });
      
      console.log('🗑️ Cleared all stored data');
      return true;
    } catch (error) {
      console.error('❌ Failed to clear all data:', error);
      return false;
    }
  }
  
  /**
   * Get statistics
   */
  getStats() {
    return {
      ...this.stats,
      persistenceEnabled: this.enablePersistence,
      storageAvailable: this.isLocalStorageAvailable(),
      uptime: this.stats.lastLoadTime ? this.formatUptime(Date.now() - this.stats.lastLoadTime) : 'N/A'
    };
  }
  
  /**
   * Format uptime
   */
  formatUptime(ms) {
    const seconds = Math.floor(ms / 1000);
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);
    
    if (hours > 0) {
      return `${hours} hours`;
    } else if (minutes > 0) {
      return `${minutes} minutes`;
    } else {
      return `${seconds} seconds`;
    }
  }
  
  /**
   * Reset statistics
   */
  resetStats() {
    this.stats = {
      totalSaves: 0,
      totalLoads: 0,
      totalConflicts: 0,
      totalReconciliations: 0,
      lastSyncTime: null,
      lastLoadTime: null
    };
    
    console.log('📊 Reset state manager statistics');
  }
}

export default StateManager;

